import { useState } from "react";
import { Download, Check, Loader2, CheckCircle2, FileText, AlertTriangle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { ExpirationAlert, ExpirationBadge, getExpiredDocuments } from "./ExpirationAlert";

interface LinkedDocument {
  id: string;
  label: string;
  doc_type: string | null;
  document_id: string;
  sort_order: number;
  document: {
    id: string;
    title: string;
    file_name: string;
    file_path: string;
    file_size: number | null;
    mime_type: string;
    expires_at?: string | null;
  };
}

interface DownloadAssistantDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  demarcheTitle: string;
  linkedDocuments: LinkedDocument[];
}

// Helper to slugify labels for file names
function slugify(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .toLowerCase()
    .substring(0, 50);
}

export function DownloadAssistantDialog({
  open,
  onOpenChange,
  demarcheTitle,
  linkedDocuments,
}: DownloadAssistantDialogProps) {
  const [downloadedIds, setDownloadedIds] = useState<Set<string>>(new Set());
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [isDownloadingAll, setIsDownloadingAll] = useState(false);
  const [showExpirationWarning, setShowExpirationWarning] = useState(false);
  const [pendingAction, setPendingAction] = useState<'single' | 'all' | null>(null);
  const [pendingDoc, setPendingDoc] = useState<{ doc: LinkedDocument; index: number } | null>(null);

  const downloadedCount = downloadedIds.size;
  const totalCount = linkedDocuments.length;
  const progressPercent = totalCount > 0 ? Math.round((downloadedCount / totalCount) * 100) : 0;

  // Check for expired documents
  const expiredDocs = getExpiredDocuments(linkedDocuments);
  const hasExpiredDocs = expiredDocs.length > 0;

  const performDownloadSingle = async (doc: LinkedDocument, index: number) => {
    setDownloadingId(doc.id);
    
    try {
      const { data, error } = await supabase.storage
        .from('documents')
        .download(doc.document.file_path);

      if (error || !data) {
        throw new Error("Impossible de télécharger le fichier");
      }

      // Smart renaming: 01_Piece_identite.pdf
      const prefix = String(index + 1).padStart(2, '0');
      const ext = doc.document.file_name.split('.').pop() || 'pdf';
      const cleanLabel = slugify(doc.label);
      const fileName = `${prefix}_${cleanLabel}.${ext}`;

      // Trigger download
      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      // Mark as downloaded
      setDownloadedIds(prev => new Set([...prev, doc.id]));
    } catch (error: any) {
      console.error('Download error:', error);
      toast.error(error.message || "Erreur lors du téléchargement");
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDownloadSingle = async (doc: LinkedDocument, index: number) => {
    // Check if this specific document is expired
    const isDocExpired = doc.document.expires_at && 
      new Date(doc.document.expires_at) < new Date();

    if (isDocExpired && !downloadedIds.has(doc.id)) {
      setPendingDoc({ doc, index });
      setPendingAction('single');
      setShowExpirationWarning(true);
      return;
    }

    await performDownloadSingle(doc, index);
  };

  const handleDownloadAll = async () => {
    // Check for expired documents before downloading all
    if (hasExpiredDocs && downloadedCount === 0) {
      setPendingAction('all');
      setShowExpirationWarning(true);
      return;
    }

    await performDownloadAll();
  };

  const performDownloadAll = async () => {
    setIsDownloadingAll(true);
    
    for (let i = 0; i < linkedDocuments.length; i++) {
      const doc = linkedDocuments[i];
      if (!downloadedIds.has(doc.id)) {
        await performDownloadSingle(doc, i);
        // Small delay between downloads
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }
    
    setIsDownloadingAll(false);
    toast.success("Tous les documents ont été téléchargés");
  };

  const handleContinueWithExpired = async () => {
    setShowExpirationWarning(false);
    
    if (pendingAction === 'single' && pendingDoc) {
      await performDownloadSingle(pendingDoc.doc, pendingDoc.index);
    } else if (pendingAction === 'all') {
      await performDownloadAll();
    }
    
    setPendingAction(null);
    setPendingDoc(null);
  };

  const handleCancelExpired = () => {
    setShowExpirationWarning(false);
    setPendingAction(null);
    setPendingDoc(null);
  };

  const handleClose = () => {
    setDownloadedIds(new Set());
    setDownloadingId(null);
    setIsDownloadingAll(false);
    setShowExpirationWarning(false);
    setPendingAction(null);
    setPendingDoc(null);
    onOpenChange(false);
  };

  const toggleDownloaded = (docId: string) => {
    setDownloadedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(docId)) {
        newSet.delete(docId);
      } else {
        newSet.add(docId);
      }
      return newSet;
    });
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Download className="w-5 h-5 text-primary" />
            Assistant de téléchargement
          </DialogTitle>
          <DialogDescription>
            Téléchargez les documents un par un pour les uploader sur un site externe (ANTS, Ameli, etc.)
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Expiration warning */}
          {showExpirationWarning && (
            <ExpirationAlert
              expiredDocs={pendingAction === 'single' && pendingDoc 
                ? expiredDocs.filter(d => d.id === pendingDoc.doc.id)
                : expiredDocs
              }
              onContinue={handleContinueWithExpired}
              onCancel={handleCancelExpired}
            />
          )}

          {/* Static warning banner if expired docs exist */}
          {hasExpiredDocs && !showExpirationWarning && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-destructive/10 border border-destructive/30">
              <AlertTriangle className="w-4 h-4 text-destructive shrink-0" />
              <p className="text-sm text-destructive">
                <strong>{expiredDocs.length}</strong> document{expiredDocs.length > 1 ? 's' : ''} expiré{expiredDocs.length > 1 ? 's' : ''} dans ce dossier
              </p>
            </div>
          )}

          {/* Progress */}
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Progression</span>
              <span className="font-medium">{downloadedCount}/{totalCount} téléchargés</span>
            </div>
            <Progress value={progressPercent} className="h-2" />
          </div>

          {/* Document list */}
          <ScrollArea className="h-[300px] pr-4">
            <div className="space-y-2">
              {linkedDocuments.map((doc, index) => {
                const isDownloaded = downloadedIds.has(doc.id);
                const isDownloading = downloadingId === doc.id;
                const isExpired = doc.document.expires_at && 
                  new Date(doc.document.expires_at) < new Date();
                
                return (
                  <div
                    key={doc.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                      isDownloaded 
                        ? 'bg-accent/50 border-primary/30' 
                        : isExpired
                          ? 'bg-destructive/5 border-destructive/30'
                          : 'bg-card hover:bg-muted/50'
                    }`}
                  >
                    {/* Checkbox to track */}
                    <Checkbox
                      checked={isDownloaded}
                      onCheckedChange={() => toggleDownloaded(doc.id)}
                      className="shrink-0"
                    />
                    
                    {/* Index */}
                    <Badge variant="outline" className="shrink-0 text-xs font-mono">
                      {String(index + 1).padStart(2, '0')}
                    </Badge>
                    
                    {/* Icon */}
                    {isDownloaded ? (
                      <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />
                    ) : isExpired ? (
                      <AlertTriangle className="w-4 h-4 text-destructive shrink-0" />
                    ) : (
                      <FileText className="w-4 h-4 text-muted-foreground shrink-0" />
                    )}
                    
                    {/* Label */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className={`text-sm font-medium truncate ${
                          isDownloaded ? 'text-primary' : isExpired ? 'text-destructive' : ''
                        }`}>
                          {doc.label}
                        </p>
                        <ExpirationBadge expiresAt={doc.document.expires_at} compact />
                      </div>
                      <p className="text-xs text-muted-foreground truncate">
                        {doc.document.file_name}
                      </p>
                    </div>
                    
                    {/* Download button */}
                    <Button
                      variant={isDownloaded ? "ghost" : isExpired ? "destructive" : "outline"}
                      size="sm"
                      onClick={() => handleDownloadSingle(doc, index)}
                      disabled={isDownloading || isDownloadingAll}
                      className="shrink-0"
                    >
                      {isDownloading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : isDownloaded ? (
                        <Check className="w-4 h-4 text-primary" />
                      ) : (
                        <Download className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                );
              })}
            </div>
          </ScrollArea>

          {/* Confirmation buttons when showing warning */}
          {showExpirationWarning ? (
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={handleCancelExpired} className="flex-1">
                Annuler
              </Button>
              <Button 
                variant="destructive" 
                onClick={handleContinueWithExpired} 
                className="flex-1"
              >
                Continuer quand même
              </Button>
            </div>
          ) : (
            <>
              {/* Actions */}
              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={handleClose}
                  className="flex-1"
                >
                  Fermer
                </Button>
                <Button
                  onClick={handleDownloadAll}
                  disabled={isDownloadingAll || downloadedCount === totalCount}
                  className="flex-1"
                  variant={hasExpiredDocs ? "destructive" : "default"}
                >
                  {isDownloadingAll ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Téléchargement...
                    </>
                  ) : downloadedCount === totalCount ? (
                    <>
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Terminé
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4 mr-2" />
                      Tout télécharger
                    </>
                  )}
                </Button>
              </div>
              
              {/* Tip */}
              <p className="text-xs text-muted-foreground text-center">
                💡 Les fichiers sont renommés automatiquement (01_Nom_document.pdf) pour faciliter l'upload
              </p>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
